import type { webApi } from '@mi-front/api-clients';

export interface ProjectRenderedVideoUrlResult {
  /** Signed video URL when available; null when not rendered or BFF did not return URLs. */
  url: string | null;
  /** Always false: URLs come from list payload (`renderedVideo`), not a separate request. */
  isLoading: boolean;
}

export type GetProjectRenderedVideoUrlMode = 'download' | 'playback';

export interface GetProjectRenderedVideoUrlOptions {
  /**
   * `download`: BFF `renderedVideo.downloadUrl`.
   * `playback`: BFF `renderedVideo.url` with `response-content-disposition` stripped when present.
   */
  mode: GetProjectRenderedVideoUrlMode;
}

/**
 * Removes the `response-content-disposition` query parameter when the storage signed URL
 * still includes it (e.g. so video.js can play inline instead of treating the response as a download).
 */
function withoutResponseContentDispositionQuery(url: string): string {
  try {
    const parsed = new URL(url);
    if (!parsed.searchParams.has('response-content-disposition')) {
      return url;
    }
    parsed.searchParams.delete('response-content-disposition');
    return parsed.toString();
  } catch {
    return url;
  }
}

export type EnrichedProjectForRenderedUrl = Pick<
  webApi.EnrichedVideoEditorProjectListItemDto,
  'renderJob' | 'renderedVideo'
> | null;

/**
 * Returns signed video URLs from BFF-enriched project payload (`renderedVideo`) when the render job completed.
 */
export function getProjectRenderedVideoUrl(
  project: EnrichedProjectForRenderedUrl,
  options: GetProjectRenderedVideoUrlOptions,
): ProjectRenderedVideoUrlResult {
  const { mode } = options;
  const isRenderCompleted = !!project?.renderJob && project.renderJob.status === 'COMPLETED';

  if (!project || !isRenderCompleted || !project.renderedVideo) {
    return { url: null, isLoading: false };
  }

  const rawUrl =
    mode === 'download' ? project.renderedVideo.downloadUrl : project.renderedVideo.url;

  if (!rawUrl) {
    return { url: null, isLoading: false };
  }

  const url = mode === 'playback' ? withoutResponseContentDispositionQuery(rawUrl) : rawUrl;

  return {
    url,
    isLoading: false,
  };
}
